import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { PlayArrowRounded } from "@mui/icons-material";

import { todayLongLabel } from "../../../../utils/format-date";
import FeedbackInvite from "./FeedbackInvite";

// ----------------------------------------------------------------------

interface HeroGreetingProps {
  /** Nome completo — exibimos só o primeiro nome */
  name: string;
  onStartConsult: () => void;
  onHistory: () => void;
}

export default function HeroGreeting({ name, onStartConsult, onHistory }: HeroGreetingProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const firstName = name.trim().split(/\s+/)[0] || "";

  if (isMobile) {
    return (
      <Box
        sx={{
          background: theme.palette.brand.heroMobile,
          borderRadius: "18px",
          p: "20px",
          mb: "18px",
          color: theme.palette.common.white,
        }}
      >
        <Typography
          component="h2"
          sx={{ fontFamily: theme.typography.fontFamilyDisplay, fontSize: 26, lineHeight: 1.15 }}
        >
          Olá, {firstName}
        </Typography>
        <Typography sx={{ mt: "4px", mb: "8px", fontSize: "12.5px", color: "rgba(255,255,255,0.85)" }}>
          Inicie uma nova consulta em segundos.
        </Typography>
        <Box sx={{ mb: "14px" }}>
          <FeedbackInvite onBrand />
        </Box>
        <Button
          onClick={onStartConsult}
          startIcon={<PlayArrowRounded />}
          sx={{
            height: 42,
            px: "18px",
            backgroundColor: theme.palette.common.white,
            color: "secondary.main",
            "&:hover": { backgroundColor: theme.palette.brand.cream },
          }}
        >
          Iniciar consulta
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: theme.palette.brand.heroBg,
        border: `1px solid ${theme.palette.brand.heroBorder}`,
        borderRadius: "18px",
        p: "30px 34px",
        mb: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 3,
      }}
    >
      <Box>
        <Typography
          sx={{
            mb: 1,
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "1.2px",
            textTransform: "uppercase",
            color: "primary.main",
          }}
        >
          {todayLongLabel()}
        </Typography>
        <Typography
          component="h2"
          sx={{ fontFamily: theme.typography.fontFamilyDisplay, fontSize: 34, lineHeight: 1.1, color: "text.primary" }}
        >
          Olá, {firstName}
        </Typography>
        <Typography sx={{ mt: "6px", maxWidth: 420, fontSize: "14px", color: theme.palette.brand.heroText }}>
          Pronta para prescrever? Inicie uma nova consulta ou continue de onde parou.
        </Typography>
        <Box sx={{ mt: "10px" }}>
          <FeedbackInvite />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
        <Button variant="contained" onClick={onStartConsult} startIcon={<PlayArrowRounded />} sx={{ height: 50, px: "22px" }}>
          Iniciar consulta
        </Button>
        <Button
          onClick={onHistory}
          sx={{
            height: 50,
            px: "20px",
            backgroundColor: "background.paper",
            color: "secondary.main",
            border: `1.5px solid ${theme.palette.divider}`,
            "&:hover": { backgroundColor: theme.palette.brand.surface2 },
          }}
        >
          Histórico
        </Button>
      </Box>
    </Box>
  );
}
